/**
 * Quick Correction Panel
 * 
 * Inline correction panel for fast transaction editing
 * Provides one-click account selection and keyboard shortcuts
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  CheckCircle, 
  XCircle, 
  Edit3, 
  Save, 
  RotateCcw,
  Keyboard
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface QuickCorrectionPanelProps {
  transaction: {
    id: string;
    counterparty?: string;
    description?: string;
    amount?: number;
    ai_classification_confidence?: number;
    suggested_debit_account?: string;
    suggested_credit_account?: string;
    suggested_vat_code?: string;
  };
  onSave?: (correction: {
    id: string;
    debit_account: string;
    credit_account: string;
    vat_code?: string;
  }) => void;
  onCancel?: () => void;
  isVisible?: boolean;
  className?: string;
}

// Common Swiss chart of accounts
const CHART_OF_ACCOUNTS = [
  { code: '1000', name: 'Kasse' },
  { code: '1020', name: 'PostFinance Konto' },
  { code: '1100', name: 'Bankkonto' },
  { code: '1200', name: 'Debitoren' },
  { code: '1300', name: 'Vorräte' },
  { code: '1400', name: 'Anlagevermögen' },
  { code: '2000', name: 'Verbindlichkeiten' },
  { code: '2200', name: 'Kreditoren' },
  { code: '2300', name: 'Steuern' },
  { code: '2400', name: 'Kurzfristige Verbindlichkeiten' },
  { code: '3000', name: 'Eigenkapital' },
  { code: '4000', name: 'Umsatzerlöse' },
  { code: '5000', name: 'Warenaufwand' },
  { code: '6000', name: 'Betriebsaufwand' },
  { code: '6100', name: 'Personalkosten' },
  { code: '6200', name: 'Miete' },
  { code: '6300', name: 'Büroaufwand' },
  { code: '6400', name: 'Versicherungen' },
  { code: '6500', name: 'Beratungskosten' },
  { code: '6600', name: 'Werbung' },
  { code: '6700', name: 'Informatik' },
  { code: '6800', name: 'Reisekosten' },
  { code: '6900', name: 'Sonstige Betriebsaufwendungen' },
  { code: '7000', name: 'Finanzaufwand' },
  { code: '8000', name: 'Ausserordentlicher Aufwand' },
  { code: '9000', name: 'Privatentnahmen' },
];

const VAT_CODES = [
  { code: '0', name: 'Keine MWST', rate: '0%' },
  { code: '8.1', name: 'Reduziert', rate: '2.5%' },
  { code: '8.2', name: 'Unterkunft', rate: '3.7%' },
  { code: '8.3', name: 'Standard', rate: '7.7%' },
];

export const QuickCorrectionPanel = ({
  transaction,
  onSave,
  onCancel,
  isVisible = false,
  className = ''
}: QuickCorrectionPanelProps) => {
  const { toast } = useToast();
  const [debitAccount, setDebitAccount] = useState(transaction.suggested_debit_account || '');
  const [creditAccount, setCreditAccount] = useState(transaction.suggested_credit_account || '');
  const [vatCode, setVatCode] = useState(transaction.suggested_vat_code || '');
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const debitRef = useRef<HTMLButtonElement>(null);
  const creditRef = useRef<HTMLButtonElement>(null);

  // Track changes
  useEffect(() => {
    const changed = 
      debitAccount !== transaction.suggested_debit_account ||
      creditAccount !== transaction.suggested_credit_account ||
      vatCode !== transaction.suggested_vat_code;
    setHasChanges(changed);
  }, [debitAccount, creditAccount, vatCode, transaction]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 's':
            e.preventDefault();
            if (hasChanges) handleSave();
            break;
          case 'z':
            e.preventDefault();
            handleCancel();
            break;
          case 'e':
            e.preventDefault();
            setIsEditing(true);
            setTimeout(() => debitRef.current?.focus(), 100);
            break;
        }
      }
      
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, hasChanges]);

  const handleSave = () => {
    if (!debitAccount || !creditAccount) {
      toast({
        title: 'Unvollständig',
        description: 'Bitte wählen Sie Soll- und Habenkonto aus.',
        variant: 'destructive',
      });
      return;
    }

    onSave?.({
      id: transaction.id,
      debit_account: debitAccount,
      credit_account: creditAccount,
      vat_code: vatCode,
    });

    setIsEditing(false);
    setHasChanges(false);
    
    toast({
      title: 'Korrektur gespeichert',
      description: 'Transaktion wurde erfolgreich korrigiert.',
    });
  };

  const handleCancel = () => {
    setDebitAccount(transaction.suggested_debit_account || '');
    setCreditAccount(transaction.suggested_credit_account || '');
    setVatCode(transaction.suggested_vat_code || '');
    setIsEditing(false);
    setHasChanges(false);
    onCancel?.();
  };

  const handleAcceptSuggestion = () => {
    if (transaction.suggested_debit_account && transaction.suggested_credit_account) {
      handleSave();
    }
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return 'text-gray-500 bg-gray-100';
    if (confidence >= 90) return 'text-green-600 bg-green-100';
    if (confidence >= 80) return 'text-blue-600 bg-blue-100';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  const getAccountName = (code: string) => {
    return CHART_OF_ACCOUNTS.find(acc => acc.code === code)?.name || code;
  };

  const getVatName = (code: string) => {
    return VAT_CODES.find(vat => vat.code === code)?.name || code;
  };

  if (!isVisible) return null;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Edit3 className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">Schnellkorrektur</span>
          {transaction.ai_classification_confidence && (
            <Badge className={`text-xs ${getConfidenceColor(transaction.ai_classification_confidence * 100)}`}>
              {(transaction.ai_classification_confidence * 100).toFixed(0)}% KI-Confidence
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Keyboard className="h-3 w-3" />
          <span>Ctrl+E bearbeiten, Ctrl+S speichern, Esc abbrechen</span>
        </div>
      </div>

      {/* Transaction Info */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600">
          <div className="font-medium">{transaction.counterparty}</div>
          <div className="text-xs">{transaction.description}</div>
          {transaction.amount && (
            <div className="text-xs font-mono">
              CHF {transaction.amount.toFixed(2)}
            </div>
          )}
        </div>
      </div>

      {/* Correction Form */}
      <div className="space-y-3">
        {/* Debit Account */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            Sollkonto
          </label>
          {isEditing ? (
            <Select value={debitAccount} onValueChange={setDebitAccount}>
              <SelectTrigger ref={debitRef} className="h-8 text-sm">
                <SelectValue placeholder="Konto wählen" />
              </SelectTrigger>
              <SelectContent>
                {CHART_OF_ACCOUNTS.map(account => (
                  <SelectItem key={account.code} value={account.code}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{account.code}</span>
                      <span>{account.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-8 px-3 py-2 bg-gray-50 rounded-md text-sm flex items-center">
              <span className="font-mono text-xs mr-2">{debitAccount}</span>
              <span>{getAccountName(debitAccount)}</span>
            </div>
          )}
        </div>

        {/* Credit Account */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            Habenkonto
          </label>
          {isEditing ? (
            <Select value={creditAccount} onValueChange={setCreditAccount}>
              <SelectTrigger ref={creditRef} className="h-8 text-sm">
                <SelectValue placeholder="Konto wählen" />
              </SelectTrigger>
              <SelectContent>
                {CHART_OF_ACCOUNTS.map(account => (
                  <SelectItem key={account.code} value={account.code}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{account.code}</span>
                      <span>{account.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-8 px-3 py-2 bg-gray-50 rounded-md text-sm flex items-center">
              <span className="font-mono text-xs mr-2">{creditAccount}</span>
              <span>{getAccountName(creditAccount)}</span>
            </div>
          )}
        </div>

        {/* VAT Code */}
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">
            MWST-Code
          </label>
          {isEditing ? (
            <Select value={vatCode} onValueChange={setVatCode}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="MWST-Code wählen" />
              </SelectTrigger>
              <SelectContent>
                {VAT_CODES.map(vat => (
                  <SelectItem key={vat.code} value={vat.code}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{vat.code}</span>
                      <span>{vat.name} ({vat.rate})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-8 px-3 py-2 bg-gray-50 rounded-md text-sm flex items-center">
              <span className="font-mono text-xs mr-2">{vatCode}</span>
              <span>{getVatName(vatCode)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          {!isEditing && transaction.ai_classification_confidence && transaction.ai_classification_confidence > 0.9 && (
            <Button
              size="sm"
              onClick={handleAcceptSuggestion}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              KI-Vorschlag übernehmen
            </Button>
          )}
          
          {!isEditing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 className="h-3 w-3 mr-1" />
              Bearbeiten
            </Button>
          )}
        </div>

        {isEditing && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
            >
              <XCircle className="h-3 w-3 mr-1" />
              Abbrechen
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="h-3 w-3 mr-1" />
              Speichern
            </Button>
          </div>
        )}
      </div>

      {/* Change Indicator */}
      {hasChanges && (
        <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
          <RotateCcw className="h-3 w-3" />
          <span>Ungespeicherte Änderungen</span>
        </div>
      )}
    </div>
  );
};
